/* eslint-disable react-refresh/only-export-components */
import { uuid } from '../lib/uuid';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocations } from './LocationContext';

const STORAGE_KEY = 'wm-work-orders-v1';

// ─── Step templates ────────────────────────────────────────────────────────────
function makeSteps(overrides = []) {
  const defaults = [
    { id: 's1', title: 'Surface prep', done: false, completedAt: null, note: '' },
    { id: 's2', title: 'Panel alignment', done: false, completedAt: null, note: '' },
    { id: 's3', title: 'Film application', done: false, completedAt: null, note: '' },
    { id: 's4', title: 'Heat gun finish', done: false, completedAt: null, note: '' },
    { id: 's5', title: 'Final inspection', done: false, completedAt: null, note: '' },
  ];
  return defaults.map((s, i) => ({ ...s, ...(overrides[i] || {}) }));
}

// ─── Seed data ────────────────────────────────────────────────────────────────
const SEED_WORK_ORDERS = [
  {
    id: 'wo-001',
    estimateId: 'est-001',
    invoiceId: 'inv-0041',
    customerId: 'c001',
    customerName: 'Marcus Bell',
    customerPhone: '(310) 555-0142',
    vehicleId: 'v001',
    vehicleLabel: '2023 Tesla Model 3',
    vehicleVin: '5YJ3E1EA1PF123456',
    status: 'in_progress',
    assignedTo: ['Jamie K.', 'Alex R.'],
    steps: makeSteps([
      { done: true, completedAt: '2025-01-21T09:00:00Z' },
      { done: true, completedAt: '2025-01-21T11:00:00Z' },
      { done: true, completedAt: '2025-01-21T14:00:00Z' },
    ]),
    photos: [],
    comms: [],
    timeEntries: [],
    signature: null,
    notes: 'Matte finish. Handle charge port area carefully.',
    scheduledStart: '2025-01-21T08:00:00Z',
    completedAt: null,
    createdBy: 'Alex R.',
    createdAt: '2025-01-20T15:00:00Z',
    updatedAt: '2025-01-21T14:30:00Z',
  },
  {
    id: 'wo-002',
    estimateId: 'est-002',
    invoiceId: null,
    customerId: 'c002',
    customerName: 'Devon Walsh',
    customerPhone: '(424) 555-0293',
    vehicleId: 'v002',
    vehicleLabel: '2022 BMW M4',
    vehicleVin: 'WBS43AY01NCK98765',
    status: 'in_progress',
    assignedTo: ['Alex R.'],
    steps: makeSteps([
      { done: true, completedAt: '2025-01-26T09:30:00Z' },
      { done: true, completedAt: '2025-01-26T10:45:00Z' },
    ]),
    photos: [],
    comms: [],
    timeEntries: [],
    signature: null,
    notes: 'Full satin black. Customer is on-site during application.',
    scheduledStart: '2025-01-26T08:00:00Z',
    completedAt: null,
    createdBy: 'Jamie K.',
    createdAt: '2025-01-25T17:00:00Z',
    updatedAt: '2025-01-26T11:00:00Z',
  },
  {
    id: 'wo-003',
    estimateId: 'est-003',
    invoiceId: null,
    customerId: 'c003',
    customerName: 'Tina Marsh',
    customerPhone: '(818) 555-0381',
    vehicleId: 'v003',
    vehicleLabel: '2021 Ford F-150 Raptor',
    vehicleVin: '1FTFW1RG9MFC87321',
    status: 'pending',
    assignedTo: ['Sam T.'],
    steps: makeSteps(),
    photos: [],
    comms: [],
    timeEntries: [],
    signature: null,
    notes: 'Partial roof + hood. Surface in good condition from prior removal.',
    scheduledStart: '2025-02-14T08:00:00Z',
    completedAt: null,
    createdBy: 'Sam T.',
    createdAt: '2025-02-11T10:00:00Z',
    updatedAt: '2025-02-11T10:00:00Z',
  },
  {
    id: 'wo-004',
    estimateId: 'est-008',
    invoiceId: null,
    customerId: 'c007',
    customerName: 'Brett Tanaka',
    customerPhone: '(562) 555-0771',
    vehicleId: 'v008',
    vehicleLabel: '2023 Ford Transit (Fleet)',
    vehicleVin: '1FTBR3X85PKA22222',
    status: 'done',
    assignedTo: ['Alex R.', 'Morgan L.'],
    steps: makeSteps([
      { done: true, completedAt: '2025-02-26T09:00:00Z' },
      { done: true, completedAt: '2025-02-26T11:00:00Z' },
      { done: true, completedAt: '2025-02-26T14:00:00Z' },
      { done: true, completedAt: '2025-02-26T16:00:00Z' },
      { done: true, completedAt: '2025-02-26T17:00:00Z' },
    ]),
    photos: [],
    comms: [],
    timeEntries: [],
    signature: {
      dataUrl: null,
      signedAt: '2025-02-26T17:30:00Z',
      signerName: 'Brett Tanaka',
    },
    notes: 'Fleet wrap unit 2. Corporate branding, logo placement verified.',
    scheduledStart: '2025-02-26T08:00:00Z',
    completedAt: '2025-02-26T17:30:00Z',
    createdBy: 'Alex R.',
    createdAt: '2025-02-25T12:00:00Z',
    updatedAt: '2025-02-26T17:30:00Z',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch { /* ignore */ }
  return SEED_WORK_ORDERS;
}

function saveToStorage(orders) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(orders)); } catch { /* ignore */ }
}

// ─── Context ──────────────────────────────────────────────────────────────────
const WorkOrderContext = createContext(null);

// ─── Provider ─────────────────────────────────────────────────────────────────
export function WorkOrderProvider({ children }) {
  const { activeLocationId: _activeLocationId } = useLocations();

  const [workOrders, setWorkOrders] = useState(() => {
    if (import.meta.env.VITE_DEV_AUTH === '1') return SEED_WORK_ORDERS;
    return loadFromStorage();
  });

  // Write-through persistence
  useEffect(() => { saveToStorage(workOrders); }, [workOrders]);

  // NOTE: Supabase sync skipped — work_orders table created in migration but
  // orgId credentials are not yet configured for this module. Wire up in a
  // future phase once the supabase workOrderService is built.

  // ─── Filtered view ──────────────────────────────────────────────────────────
  // Work orders don't yet carry a locationId — filter is a no-op for 'all'
  const filteredWorkOrders = workOrders; // expand filtering when locationId is added

  // ─── addWorkOrder ───────────────────────────────────────────────────────────
  const addWorkOrder = useCallback((data = {}) => {
    const now = new Date().toISOString();
    const newWO = {
      id: uuid(),
      estimateId: null,
      invoiceId: null,
      customerId: '',
      customerName: '',
      customerPhone: '',
      vehicleId: '',
      vehicleLabel: '',
      vehicleVin: '',
      status: 'pending',
      assignedTo: [],
      steps: makeSteps(),
      photos: [],
      comms: [],
      timeEntries: [],
      signature: null,
      notes: '',
      scheduledStart: null,
      completedAt: null,
      createdBy: '',
      createdAt: now,
      updatedAt: now,
      ...data,
    };
    setWorkOrders(prev => [newWO, ...prev]);
    return newWO;
  }, []);

  // ─── updateWorkOrder ────────────────────────────────────────────────────────
  const updateWorkOrder = useCallback((id, patch) => {
    setWorkOrders(prev =>
      prev.map(wo =>
        wo.id !== id ? wo : { ...wo, ...patch, updatedAt: new Date().toISOString() }
      )
    );
  }, []);

  // ─── deleteWorkOrder ────────────────────────────────────────────────────────
  const deleteWorkOrder = useCallback((id) => {
    setWorkOrders(prev => prev.filter(wo => wo.id !== id));
  }, []);

  // ─── getWorkOrderById ───────────────────────────────────────────────────────
  const getWorkOrderById = useCallback((id) => {
    return workOrders.find(wo => wo.id === id) ?? null;
  }, [workOrders]);

  // ─── completeStep ────────────────────────────────────────────────────────────
  const completeStep = useCallback((workOrderId, stepId) => {
    setWorkOrders(prev =>
      prev.map(wo => {
        if (wo.id !== workOrderId) return wo;
        const steps = wo.steps.map(s =>
          s.id === stepId
            ? { ...s, done: !s.done, completedAt: !s.done ? new Date().toISOString() : null }
            : s
        );
        return { ...wo, steps, updatedAt: new Date().toISOString() };
      })
    );
  }, []);

  // ─── setWorkOrderStatus ──────────────────────────────────────────────────────
  const setWorkOrderStatus = useCallback((id, status) => {
    const now = new Date().toISOString();
    setWorkOrders(prev =>
      prev.map(wo =>
        wo.id !== id ? wo : {
          ...wo,
          status,
          completedAt: status === 'done' ? now : wo.completedAt,
          updatedAt: now,
        }
      )
    );
  }, []);

  const value = {
    workOrders: filteredWorkOrders,
    allWorkOrders: workOrders,
    addWorkOrder,
    updateWorkOrder,
    deleteWorkOrder,
    getWorkOrderById,
    completeStep,
    setWorkOrderStatus,
  };

  return (
    <WorkOrderContext.Provider value={value}>
      {children}
    </WorkOrderContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useWorkOrders() {
  const ctx = useContext(WorkOrderContext);
  if (!ctx) throw new Error('useWorkOrders must be used within WorkOrderProvider');
  return ctx;
}
